import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { MiniConversaSheet } from '@/components/qualidade/MiniConversaSheet';
import { VincularConversaDialog, type AlvoConversa } from '@/components/kanban/VincularConversaDialog';
import { useQueryClient } from '@tanstack/react-query';

type Ctx = {
  /** Abre a conversa do cliente daquele card. Sem `conversaId`, abre o passo
   *  de vincular o número antes. */
  abrir: (alvo: AlvoConversa) => void;
  fechar: () => void;
};

const ConversaPedidoContext = createContext<Ctx>({
  abrir: () => { console.warn('[ConversaPedidoContext] sem Provider'); },
  fechar: () => {},
});

export const useConversaPedido = () => useContext(ConversaPedidoContext);

/* A GAVETA DA CONVERSA — clique no card do Kanban (Douglas 17/09/2026).
   Antes o balão do card fazia window.open do Inbox em outra aba e a pessoa
   perdia o Kanban de vista. A conversa é a MESMA do Inbox, com a MESMA régua de
   visão (Lei da Visão do Inbox, igual para CPF e CNPJ) — só que trazida para o
   lado do card em vez de substituir a tela.

   O estado mora aqui, no provider, e não no card: são até 7 mil cards na tela e
   uma gaveta por card seria uma gaveta por card. */
export const ConversaPedidoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alvo, setAlvo] = useState<AlvoConversa | null>(null);
  const queryClient = useQueryClient();

  const abrir = useCallback((a: AlvoConversa) => setAlvo(a), []);
  const fechar = useCallback(() => setAlvo(null), []);

  const valor = useMemo<Ctx>(() => ({ abrir, fechar }), [abrir, fechar]);

  const contexto = alvo
    ? {
        titulo: 'Você está falando sobre',
        detalhe: [alvo.numeroPedido, alvo.fila, alvo.clienteNome].filter(Boolean).join(' · '),
      }
    : null;

  return (
    <ConversaPedidoContext.Provider value={valor}>
      {children}
      {alvo && alvo.conversaId && (
        <MiniConversaSheet
          key={alvo.conversaId}
          open
          telefone=""
          conversationId={alvo.conversaId}
          contexto={contexto}
          onClose={fechar}
        />
      )}
      {alvo && !alvo.conversaId && (
        <VincularConversaDialog
          alvo={alvo}
          open
          onClose={fechar}
          onVinculada={(conversaId) => {
            /* O card mostra o rosto a partir de v_pedido_conversa — sem isto
               ele continuaria dizendo "sem conversa" até a próxima recarga. */
            void queryClient.invalidateQueries({ queryKey: ['kanban-rostos'] });
            setAlvo((a) => (a ? { ...a, conversaId } : a));
          }}
        />
      )}
    </ConversaPedidoContext.Provider>
  );
};
